import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Download, ArrowRight, Loader2, CheckCircle2, X, ShoppingCart, Mail, Sparkles, Star, Truck, Users, Plus, Minus, MessageCircle, Gift, Ticket, BadgePercent, Layers } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useCopy, getCurrentVariant } from "@/lib/copyVariant";
import { img } from "@/lib/img";
import { trackCart } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = ["US","CA","GB","AU","IN","AE","SG","DE","FR","NL","IE"];

const fadeUp = { hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0 } };
const inputCls = "bg-white border-[#464646]/15 text-[#464646] placeholder:text-[#464646]/35 rounded-full px-5 h-11 text-sm focus-visible:ring-[#96aeb4]/40";

// ---------- Free lead form (waitlist + ebook summary) ----------
function LeadForm({ kind, onSuccess, submitLabel, submitIcon, loadingLabel }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !last.trim() || !email.trim()) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/${kind}`, {
        first_name: first.trim(), last_name: last.trim(), email: email.trim(),
        aud_variant: getCurrentVariant(),
      });
      // GA4 client-side lead event with audience attribution
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            lead_type: kind,
            audience_variant: getCurrentVariant(),
          });
        }
      } catch (_) { /* noop */ }
      toast.success(res.data.message);
      onSuccess?.();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2" data-testid={`${kind}-form`}>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} className={inputCls + " h-10 text-xs"} data-testid={`${kind}-first-input`} />
        <Input placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} className={inputCls + " h-10 text-xs"} data-testid={`${kind}-last-input`} />
      </div>
      <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " h-10 text-xs"} data-testid={`${kind}-email-input`} />
      <button type="submit" disabled={loading} className="w-full border border-[#464646]/15 bg-white hover:bg-[#fff8f3] text-[#464646] rounded-full py-2.5 text-xs font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`${kind}-submit-btn`}>
        {loading ? <Loader2 className="animate-spin" size={14} /> : submitIcon}
        {loading ? loadingLabel : submitLabel}
      </button>
    </form>
  );
}

// ---------- Paid checkout form ----------
function CheckoutForm({ productId, price, requireShipping, quantity = 1 }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [refStatus, setRefStatus] = useState({ state: "idle", first_name: "" }); // idle | checking | valid | invalid
  const [autoReferral, setAutoReferral] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Detect a Rewardful cookie-tracked referral once - gives free shipping silently.
  useEffect(() => {
    if (typeof window === "undefined" || !window.rewardful) return;
    try {
      window.rewardful("ready", () => {
        if (window.Rewardful && window.Rewardful.referral) setAutoReferral(true);
      });
    } catch (_) { /* noop */ }
  }, []);

  // Validate manual code on blur / debounce
  const validateCode = useCallback(async (code) => {
    const c = (code || "").trim();
    if (!c) { setRefStatus({ state: "idle", first_name: "" }); return; }
    setRefStatus({ state: "checking", first_name: "" });
    try {
      const res = await axios.get(`${API}/affiliate/validate-code/${encodeURIComponent(c)}`);
      if (res.data?.valid) {
        setRefStatus({ state: "valid", first_name: res.data.first_name || "" });
      } else {
        setRefStatus({ state: "invalid", first_name: "" });
      }
    } catch {
      setRefStatus({ state: "invalid", first_name: "" });
    }
  }, []);

  const isReferred = autoReferral || refStatus.state === "valid";
  // Numeric price (strip leading $) for shipping math
  const numericPrice = parseFloat((price || "").toString().replace(/[^0-9.]/g, "")) || 0;
  const shippingFee = requireShipping && !isReferred ? 5 : 0;
  const totalDisplay = `$${(numericPrice + shippingFee).toFixed(2)}`;

  const submit = async (e) => {
    e.preventDefault();
    const baseFields = ["first_name", "last_name", "email"];
    const shipFields = requireShipping ? ["line1", "city", "postal_code", "country"] : [];
    for (const f of [...baseFields, ...shipFields]) {
      if (!form[f]?.trim()) { toast.error("Please complete all required fields"); return; }
    }
    if (refCode.trim() && refStatus.state === "invalid") {
      toast.error("That referral code isn't valid. Clear it or try another.");
      return;
    }
    setLoading(true);
    try {
      const body = {
        product_id: productId,
        origin_url: window.location.origin,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        quantity,
        aud_variant: getCurrentVariant(),
      };
      // Track "clicked checkout" telemetry BEFORE we hit Stripe. Fire-and-forget.
      trackCart("clicked_checkout", {
        email: body.email,
        product_id: productId,
        quantity,
        first_name: body.first_name,
        last_name: body.last_name,
        aud_variant: body.aud_variant,
      });
      // Rewardful auto-tracking via cookie (UUID)
      try {
        const ref = typeof window !== "undefined" && window.Rewardful && window.Rewardful.referral;
        if (ref) body.referral = ref;
      } catch (_) { /* noop */ }
      // Manual referral code typed by buyer (validated against our DB)
      if (refStatus.state === "valid" && refCode.trim()) {
        body.referral_code = refCode.trim();
      }
      if (requireShipping) {
        body.shipping = {
          line1: form.line1.trim(), line2: form.line2.trim(),
          city: form.city.trim(), state: form.state.trim(),
          postal_code: form.postal_code.trim(), country: form.country,
          phone: form.phone.trim(),
        };
      }
      const res = await axios.post(`${API}/payments/checkout`, body);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`checkout-${productId}-form`}>
      <div className="grid grid-cols-2 gap-2.5">
        <Input placeholder="First name *" value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid={`checkout-${productId}-first`} />
        <Input placeholder="Last name *" value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid={`checkout-${productId}-last`} />
      </div>
      <Input
        type="email"
        placeholder="Email *"
        value={form.email}
        onChange={set("email")}
        onBlur={() => {
          // Fire add_to_cart the first time the user tabs out of a valid email.
          if (form.email && form.email.includes("@")) {
            trackCart("add_to_cart", {
              email: form.email,
              product_id: productId,
              quantity,
              first_name: form.first_name,
              last_name: form.last_name,
              aud_variant: getCurrentVariant(),
            });
          }
        }}
        className={inputCls}
        data-testid={`checkout-${productId}-email`}
      />

      {requireShipping && (
        <>
          <div className="pt-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#96aeb4] font-semibold mb-2">Shipping</p>
            <Input placeholder="Address line 1 *" value={form.line1} onChange={set("line1")} className={inputCls} data-testid={`checkout-${productId}-line1`} />
          </div>
          <Input placeholder="Apt, suite, etc. (optional)" value={form.line2} onChange={set("line2")} className={inputCls} data-testid={`checkout-${productId}-line2`} />
          <div className="grid grid-cols-2 gap-2.5">
            <Input placeholder="City *" value={form.city} onChange={set("city")} className={inputCls} data-testid={`checkout-${productId}-city`} />
            <Input placeholder="State / Region" value={form.state} onChange={set("state")} className={inputCls} data-testid={`checkout-${productId}-state`} />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Input placeholder="Postal code *" value={form.postal_code} onChange={set("postal_code")} className={inputCls} data-testid={`checkout-${productId}-postal`} />
            <select value={form.country} onChange={set("country")} className={`${inputCls} appearance-none pr-9`} data-testid={`checkout-${productId}-country`}>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Input placeholder="Phone (optional, helps with delivery)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid={`checkout-${productId}-phone`} />
        </>
      )}

      {/* Referral code + shipping summary (deck/bundle only - physical products) */}
      {requireShipping && (
        <div className="pt-2" data-testid={`checkout-${productId}-referral-block`}>
          {autoReferral ? (
            <div className="rounded-2xl p-3.5 border border-[#a6c1a8]/40 bg-gradient-to-br from-[#a6c1a8]/15 via-white to-[#96aeb4]/10 flex items-center gap-3"
                 data-testid={`checkout-${productId}-auto-referral-pill`}>
              <span className="shrink-0 w-9 h-9 rounded-full bg-[#587e62] flex items-center justify-center">
                <CheckCircle2 size={16} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-[#464646] leading-tight">Referral applied · Free shipping unlocked</p>
                <p className="text-[10px] text-[#464646]/55 mt-0.5">You arrived via an affiliate link. Commission will be credited automatically.</p>
              </div>
            </div>
          ) : !refOpen ? (
            <button type="button" onClick={() => setRefOpen(true)}
              className="w-full group rounded-2xl p-3.5 border border-dashed border-[#96aeb4]/50 hover:border-[#96aeb4] bg-gradient-to-br from-[#a6c1a8]/8 to-[#96aeb4]/8 hover:from-[#a6c1a8]/15 hover:to-[#96aeb4]/15 flex items-center gap-3 transition-all text-left"
              data-testid={`checkout-${productId}-referral-toggle`}>
              <span className="shrink-0 w-9 h-9 rounded-full bg-white border border-[#96aeb4]/30 flex items-center justify-center group-hover:bg-[#96aeb4] group-hover:border-[#96aeb4] transition-colors">
                <Ticket size={16} className="text-[#96aeb4] group-hover:text-white transition-colors" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-[#464646] leading-tight">Have a referral code?</p>
                <p className="text-[10px] text-[#464646]/55 mt-0.5">Get <span className="text-[#587e62] font-semibold">FREE shipping</span> when you apply a valid code.</p>
              </div>
              <ArrowRight size={14} className="text-[#96aeb4] group-hover:translate-x-0.5 transition-transform" />
            </button>
          ) : (
            <div className="rounded-2xl p-3.5 border border-[#96aeb4]/40 bg-gradient-to-br from-[#a6c1a8]/10 via-white to-[#96aeb4]/8 space-y-2">
              <div className="flex items-center gap-2">
                <Gift size={14} className="text-[#96aeb4]" />
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#96aeb4] font-semibold">Referral Code</p>
                <button type="button" onClick={() => { setRefOpen(false); setRefCode(""); setRefStatus({ state: "idle", first_name: "" }); }}
                        className="ml-auto text-[10px] text-[#464646]/45 hover:text-[#464646] underline underline-offset-2">
                  Skip
                </button>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="Enter code"
                    value={refCode}
                    onChange={(e) => { setRefCode(e.target.value); if (refStatus.state !== "idle") setRefStatus({ state: "idle", first_name: "" }); }}
                    onBlur={(e) => { if (refStatus.state === "idle" && e.target.value.trim()) validateCode(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); validateCode(refCode); } }}
                    className={inputCls + " pr-9 uppercase tracking-wider bg-white"}
                    disabled={refStatus.state === "valid"}
                    data-testid={`checkout-${productId}-referral-input`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {refStatus.state === "valid" && <CheckCircle2 className="text-[#587e62]" size={16} data-testid={`checkout-${productId}-referral-valid`} />}
                    {refStatus.state === "invalid" && <X className="text-[#b34c4c]" size={16} data-testid={`checkout-${productId}-referral-invalid`} />}
                  </div>
                </div>
                {refStatus.state === "valid" ? (
                  <button type="button" onClick={() => { setRefCode(""); setRefStatus({ state: "idle", first_name: "" }); }}
                          className="shrink-0 rounded-full bg-[#587e62]/10 text-[#587e62] text-xs font-semibold px-4 h-11 hover:bg-[#587e62]/20 transition-colors flex items-center gap-1.5"
                          data-testid={`checkout-${productId}-referral-clear`}>
                    <CheckCircle2 size={13} /> Applied
                  </button>
                ) : (
                  <button type="button" onClick={() => validateCode(refCode)} disabled={!refCode.trim() || refStatus.state === "checking"}
                          className="shrink-0 rounded-full bg-[#464646] hover:bg-[#587e62] text-white text-xs font-semibold px-4 h-11 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                          data-testid={`checkout-${productId}-referral-apply`}>
                    {refStatus.state === "checking" ? <Loader2 className="animate-spin" size={13} /> : <ArrowRight size={13} />}
                    {refStatus.state === "checking" ? "Checking" : "Apply"}
                  </button>
                )}
              </div>
              {refStatus.state === "valid" && (
                <p className="text-[11px] text-[#587e62] flex items-center gap-1.5" data-testid={`checkout-${productId}-referral-msg`}>
                  <CheckCircle2 size={11} /> {refStatus.first_name ? `Referred by ${refStatus.first_name}. ` : ""}Free shipping unlocked!
                </p>
              )}
              {refStatus.state === "invalid" && (
                <p className="text-[11px] text-[#b34c4c]">That code isn't recognised. Double-check and try again.</p>
              )}
            </div>
          )}

          {/* Price summary */}
          <div className="mt-3 pt-3 border-t border-[#464646]/8 space-y-1" data-testid={`checkout-${productId}-summary`}>
            <div className="flex items-center justify-between text-[11px] text-[#464646]/65">
              <span>Subtotal{quantity > 1 ? ` (${quantity} decks)` : ""}</span>
              <span>${numericPrice.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]" data-testid={`checkout-${productId}-shipping-row`}>
              <span className="text-[#464646]/65">Shipping</span>
              {isReferred ? (
                <span className="text-[#587e62] font-semibold">FREE</span>
              ) : (
                <span className="text-[#464646]/65">$5.00</span>
              )}
            </div>
            <div className="flex items-center justify-between text-[13px] font-semibold text-[#464646] pt-1">
              <span>Total</span>
              <span data-testid={`checkout-${productId}-total`}>{totalDisplay}</span>
            </div>
          </div>
        </div>
      )}

      <button type="submit" disabled={loading} className="w-full bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full py-3 text-sm font-semibold hover:scale-[1.01] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`checkout-${productId}-submit`}>
        {loading ? <Loader2 className="animate-spin" size={16} /> : <ShoppingCart size={16} />}
        {loading ? "Redirecting…" : `Pay ${requireShipping ? totalDisplay : price} · Continue to Checkout`}
      </button>
      <p className="text-[10px] text-center text-[#464646]/40 mt-1">Secure payment via Stripe · {requireShipping ? "Ships in 3-5 business days" : "Instant digital delivery"}</p>
    </form>
  );
}

function CheckoutModal({ open, onClose, title, subtitle, productId, price, requireShipping, quantity = 1 }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="checkout-modal">
          <div className="absolute inset-0 bg-[#464646]/55 backdrop-blur-md" />
          <motion.div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }} onClick={(e) => e.stopPropagation()}>
            <div className="h-1.5" style={{ background: "linear-gradient(90deg, #96aeb4, #a6c1a8, #a89b94)" }} />
            <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#fff8f3] hover:bg-[#eee5db] flex items-center justify-center text-[#464646]/60 transition-colors" data-testid="checkout-close-btn">
              <X size={16} />
            </button>
            <div className="p-7 sm:p-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#96aeb4] font-semibold mb-2">{subtitle}</p>
              <h3 className="font-serif text-2xl text-[#464646] font-bold mb-5">{title}</h3>
              {quantity > 1 && (
                <div className="mb-4 -mt-2 inline-flex items-center gap-2 bg-[#a6c1a8]/15 text-[#587e62] rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="checkout-qty-pill">
                  <BadgePercent size={12} /> {quantity} decks &middot; bulk price unlocked ($25/deck)
                </div>
              )}
              <CheckoutForm productId={productId} price={price} requireShipping={requireShipping} quantity={quantity} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------- Bulk-deck inquiry modal (>99 decks → speak to team) ----------
function BulkInquiryModal({ open, onClose, defaultQuantity = 100 }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    deck_quantity: defaultQuantity, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      setDone(false);
      setForm((f) => ({ ...f, deck_quantity: defaultQuantity }));
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, defaultQuantity]);

  const submit = async (e) => {
    e.preventDefault();
    const qty = parseInt(form.deck_quantity, 10);
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Please fill in name and email"); return;
    }
    if (!qty || qty < 1) { toast.error("Please enter a valid deck quantity"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/bulk-deck`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        deck_quantity: qty,
        message: form.message.trim(),
        aud_variant: getCurrentVariant(),
      });
      toast.success(res.data.message);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="bulk-inquiry-modal">
          <div className="absolute inset-0 bg-[#464646]/55 backdrop-blur-md" />
          <motion.div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }} onClick={(e) => e.stopPropagation()}>
            <div className="h-1.5" style={{ background: "linear-gradient(90deg, #96aeb4, #a6c1a8, #a89b94)" }} />
            <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#fff8f3] hover:bg-[#eee5db] flex items-center justify-center text-[#464646]/60 transition-colors" data-testid="bulk-close-btn">
              <X size={16} />
            </button>
            <div className="p-7 sm:p-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#96aeb4] font-semibold mb-2">Bulk pricing &middot; 100+ decks</p>
              <h3 className="font-serif text-2xl text-[#464646] font-bold mb-2">Speak to our team.</h3>
              <p className="text-sm text-[#464646]/70 leading-relaxed mb-5">
                Buying for a team, retreat, conference, or as a corporate gift? Drop your details and we'll craft a tailored offer within 1–2 business days.
              </p>

              {done ? (
                <div className="text-center py-6" data-testid="bulk-success">
                  <CheckCircle2 size={42} className="mx-auto text-[#7a9d83] mb-3" />
                  <h4 className="font-serif text-xl text-[#464646] font-bold mb-2">Got it, thanks!</h4>
                  <p className="text-sm text-[#464646]/70 leading-relaxed mb-5">
                    We've sent a confirmation to <strong>{form.email}</strong>. Our team will reach out shortly.
                  </p>
                  <button onClick={onClose} className="px-6 py-2.5 bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full text-sm font-semibold transition-colors" data-testid="bulk-done-btn">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="bulk-inquiry-form">
                  <div className="grid grid-cols-2 gap-2.5">
                    <Input placeholder="First name *" value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="bulk-first" />
                    <Input placeholder="Last name *" value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="bulk-last" />
                  </div>
                  <Input type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="bulk-email" />
                  <Input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="bulk-phone" />
                  <div className="relative">
                    <Input type="number" min="1" placeholder="How many decks? *"
                      value={form.deck_quantity} onChange={set("deck_quantity")}
                      className={inputCls + " pr-16"} data-testid="bulk-qty" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#464646]/45 pointer-events-none">decks</span>
                  </div>
                  <textarea placeholder="Any context: occasion, target date, custom branding? (optional)"
                    aria-label="Order context (optional)"
                    value={form.message} onChange={set("message")}
                    className={inputCls + " min-h-[80px] py-3 resize-none rounded-2xl"}
                    rows={3}
                    data-testid="bulk-message" />
                  <button type="submit" disabled={loading}
                    className="w-full bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full py-3 text-sm font-semibold hover:scale-[1.01] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="bulk-submit">
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />}
                    {loading ? "Sending…" : "Request a bulk quote"}
                  </button>
                  <p className="text-[10px] text-center text-[#464646]/40 mt-1">No payment required &middot; We'll email you within 1–2 business days</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SuccessModal() { return null; }

// ---------- Deck card: special-case ProductCard with qty stepper + tiered pricing ----------
// Pricing:
//   qty 1        → $29 / deck
//   qty 2..99    → $25 / deck (bulk price unlocked, savings indicator shown)
//   qty 100+     → CTA flips to "Speak to our team" → opens BulkInquiryModal
function DeckCard({ onCheckout, onBulkInquiry }) {
  const [qty, setQty] = useState(1);
  const { c } = useCopy();
  const BULK_THRESHOLD = 100;
  const isBulk = qty >= 2 && qty < BULK_THRESHOLD;
  const isSpeak = qty >= BULK_THRESHOLD;
  const unitPrice = isBulk ? 25 : 29;
  const totalPrice = qty * unitPrice;
  const stepDown = () => setQty((q) => Math.max(1, q - 1));
  const stepUp = () => setQty((q) => Math.min(999, q + 1));

  const handleCta = () => {
    if (isSpeak) onBulkInquiry?.(qty);
    else onCheckout?.(qty, unitPrice, totalPrice);
  };

  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      className="relative glass rounded-2xl sm:rounded-3xl p-5 sm:p-7 md:p-8 hover:-translate-y-1 transition-transform duration-400 flex flex-col"
      data-testid="deck">
      <span className="text-[10px] uppercase tracking-[0.4em] font-semibold block mb-3 text-[#96aeb4]">{c("productDeckBadge")}</span>
      <h3 className="font-serif text-xl sm:text-2xl text-[#464646] font-medium mb-2">{c("productDeckTitle")}</h3>
      <p className="text-[#464646]/65 text-xs sm:text-sm leading-relaxed mb-4">
        {c("productDeckSub")}
      </p>

      <ul className="mb-5 space-y-1.5">
        {c("productDeckIncludes").map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-[#464646]/75">
            <CheckCircle2 size={14} className="text-[#7a9d83] shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
        {isBulk && (
          <li className="flex items-start gap-2 text-xs text-[#587e62] font-semibold">
            <BadgePercent size={14} className="text-[#7a9d83] shrink-0 mt-0.5" />
            <span>Bulk price unlocked &middot; $25 / deck</span>
          </li>
        )}
      </ul>

      <div className="mt-auto">
        {/* Quantity stepper */}
        <div className="flex items-center justify-between gap-3 mb-3 bg-[#fff8f3] rounded-full p-1.5 ring-1 ring-[#464646]/8" data-testid="deck-qty-stepper">
          <span className="text-[11px] uppercase tracking-[0.25em] text-[#464646]/55 font-semibold pl-3">Quantity</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={stepDown} disabled={qty <= 1}
              className="w-8 h-8 rounded-full bg-white hover:bg-[#96aeb4] hover:text-white text-[#464646] flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              data-testid="deck-qty-minus" aria-label="Decrease quantity">
              <Minus size={14} />
            </button>
            <input type="number" min="1" max="999" value={qty}
              aria-label="Deck quantity"
              onChange={(e) => {
                const v = parseInt(e.target.value || "1", 10);
                if (!isNaN(v)) setQty(Math.max(1, Math.min(999, v)));
              }}
              className="w-12 text-center font-bold text-[#464646] bg-transparent text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              data-testid="deck-qty-input" />
            <button type="button" onClick={stepUp}
              className="w-8 h-8 rounded-full bg-white hover:bg-[#96aeb4] hover:text-white text-[#464646] flex items-center justify-center transition-colors shadow-sm"
              data-testid="deck-qty-plus" aria-label="Increase quantity">
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Dynamic pricing */}
        {isSpeak ? (
          <div className="mb-4" data-testid="deck-price-speak">
            <p className="font-serif text-2xl sm:text-3xl font-bold text-[#464646] leading-tight">Let's talk pricing.</p>
            <p className="text-xs text-[#464646]/60 mt-1">100+ decks unlock custom corporate / bulk offers.</p>
          </div>
        ) : (
          <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1 mb-1" data-testid="deck-price">
            <span className="font-serif text-3xl sm:text-4xl font-bold text-[#464646]">${totalPrice}</span>
            {qty > 1 && (
              <>
                <span className="text-sm text-[#464646]/55">total</span>
                <span className="text-xs text-[#464646]/45">&middot;</span>
                <span className="text-xs text-[#464646]/55">${unitPrice}/deck</span>
              </>
            )}
            {isBulk && (
              <span className="text-[10px] bg-[#a6c1a8]/30 text-[#587e62] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Save ${(qty * 29) - totalPrice}</span>
            )}
          </div>
        )}
        {!isSpeak && qty === 1 && (
          <button
            type="button"
            onClick={() => setQty(2)}
            data-testid="deck-bulk-tip"
            className="group w-full mt-2 mb-3 flex items-center gap-2.5 rounded-xl border border-[#587e62]/30 bg-gradient-to-r from-[#a6c1a8]/30 via-[#a6c1a8]/15 to-transparent px-3 py-2.5 text-left hover:border-[#587e62]/55 hover:from-[#a6c1a8]/40 transition-all"
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#587e62] text-white shadow-sm">
              <BadgePercent size={13} />
            </span>
            <span className="flex-1 min-w-0 leading-tight">
              <span className="block text-[10.5px] sm:text-[11px] font-bold uppercase tracking-wider text-[#587e62]">{c("bulkCalloutEyebrow")}</span>
              <span className="block text-[11.5px] sm:text-[12px] text-[#464646]/80">
                <strong className="text-[#464646]">$25/deck</strong> &middot; save <strong className="text-[#587e62]">$4/deck</strong>
              </span>
            </span>
            <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-[#587e62] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shadow-sm group-hover:translate-x-0.5 transition-transform">
              Add <ArrowRight size={11} />
            </span>
          </button>
        )}
        {!isSpeak && qty > 1 && <div className="mb-3" />}

        <PreorderProof />

        <button onClick={handleCta}
          className={`w-full ${isSpeak ? "bg-[#a89b94] hover:bg-[#92857e]" : "bg-[#464646] hover:bg-[#96aeb4]"} text-white rounded-full py-3 text-sm font-semibold hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2`}
          data-testid={isSpeak ? "speak-team-btn" : "buy-deck-btn"}>
          {isSpeak ? <MessageCircle size={16} /> : <ShoppingCart size={16} />}
          {isSpeak ? "Speak to our team for offer" : (qty > 1 ? `Buy ${qty} decks` : c("productDeckCta"))}
        </button>
      </div>
    </motion.div>
  );
}

// ---------- Animated rotating words ----------
function RotatingWords({ words, intervalMs = 2400 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % words.length), intervalMs);
    return () => clearInterval(t);
  }, [words.length, intervalMs]);
  return (
    <span className="inline-block relative align-baseline" style={{ minWidth: "min(86vw, 700px)" }}>
      <AnimatePresence mode="wait">
        <motion.span key={i} className="text-gradient-sage italic inline-block"
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -30, filter: "blur(8px)" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// ---------- Social proof: live buyer count ----------
// Live buyer counter: starts at 50 on launch day, grows by +30 every day after.
// Number is deterministic from the date so it stays consistent across page reloads / users.
const PREORDER_BASE_DATE = Date.UTC(2026, 4, 16); // May 16, 2026 UTC - launch baseline
const PREORDER_BASE_COUNT = 50;
const PREORDER_PER_DAY = 30;

function getPreorderCount() {
  const daysElapsed = Math.max(
    0,
    Math.floor((Date.now() - PREORDER_BASE_DATE) / 86400000)
  );
  return PREORDER_BASE_COUNT + daysElapsed * PREORDER_PER_DAY;
}

function CountUp({ to, duration = 1.4 }) {
  const [n, setN] = useState(Math.max(0, to - 12));
  useEffect(() => {
    const start = performance.now();
    const from = Math.max(0, to - 12);
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{n.toLocaleString()}</>;
}

function PreorderProof() {
  const count = getPreorderCount();
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 mb-3" data-testid="deck-preorder-proof">
      <div className="inline-flex items-center gap-1.5 bg-[#a6c1a8]/20 text-[#587e62] px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.15em] whitespace-nowrap">
        <Truck size={11} /> Ships in 3 business days
      </div>
      <div className="inline-flex items-center gap-1.5 text-[11px] text-[#464646]/70 whitespace-nowrap" data-testid="deck-preorder-count">
        <motion.span
          animate={{ scale: [1, 1.18, 1] }}
          transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
          className="inline-block w-1.5 h-1.5 rounded-full bg-[#7a9d83]"
        />
        <Users size={11} className="text-[#7a9d83]" />
        <span>
          <span className="text-[#464646] font-bold"><CountUp to={count} /></span> decks already sold
        </span>
      </div>
    </div>
  );
}

// ---------- Product card ----------
function ProductCard({ icon, badge, badgeColor, title, sub, includes, price, originalPrice, savings, ctaLabel, onBuy, freeOption, accent = "charcoal", testId, mostPopular, preorderProof }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      className={`relative glass rounded-2xl sm:rounded-3xl p-5 sm:p-7 md:p-8 hover:-translate-y-1 transition-transform duration-400 flex flex-col
        ${mostPopular ? "ring-2 ring-[#96aeb4]/50 shadow-xl shadow-[#96aeb4]/15" : ""}`}
      data-testid={testId}>
      {mostPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-[#96aeb4] text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em] shadow-md">
            <Star size={11} fill="currentColor" /> Most Popular
          </div>
        </div>
      )}
      <span className={`text-[10px] uppercase tracking-[0.4em] font-semibold block mb-3 ${badgeColor}`}>{badge}</span>
      <h3 className="font-serif text-xl sm:text-2xl text-[#464646] font-medium mb-2">{title}</h3>
      <p className="text-[#464646]/65 text-xs sm:text-sm leading-relaxed mb-4">{sub}</p>

      {includes && (
        <ul className="mb-5 space-y-1.5">
          {includes.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-[#464646]/75">
              <CheckCircle2 size={14} className="text-[#7a9d83] shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto">
        <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1 mb-4">
          <span className="font-serif text-3xl sm:text-4xl font-bold text-[#464646]">{price}</span>
          {originalPrice && (
            <span className="text-sm text-[#464646]/40 line-through">{originalPrice}</span>
          )}
          {savings && (
            <span className="text-[10px] bg-[#a6c1a8]/30 text-[#587e62] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">{savings}</span>
          )}
        </div>

        {preorderProof && <PreorderProof />}

        <button onClick={onBuy}
          className={`w-full ${accent === "sage" ? "bg-[#a89b94] hover:bg-[#92857e]" : "bg-[#464646] hover:bg-[#96aeb4]"} text-white rounded-full py-3 text-sm font-semibold hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2`}
          data-testid={`buy-${testId}-btn`}>
          {icon} {ctaLabel}
        </button>

        {freeOption && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-[#464646]/10" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#464646]/40">Or free</span>
              <div className="flex-1 h-px bg-[#464646]/10" />
            </div>
            {freeOption}
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function BeginYourJourney() {
  const [modal, setModal] = useState(null);
  const [bulkModal, setBulkModal] = useState({ open: false, qty: 100 });
  const { c } = useCopy();

  useEffect(() => {
    // Old query param cleanup (in case any legacy /?session_id= URLs are around)
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled")) {
      toast.error("Payment cancelled. No charge made.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <section id="begin-journey" className="py-16 sm:py-24 md:py-32 px-5 md:px-10 relative overflow-hidden" data-testid="begin-journey"
      style={{ background: "linear-gradient(180deg, #fff8f3 0%, #f3ece4 35%, #eef4ee 65%, #fff8f3 100%)" }}>
      <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/bbahb6kp_4.png", { w: 1400 })}
        alt="" aria-hidden="true" loading="lazy" decoding="async" width="1400" height="900"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[1200px] opacity-25 pointer-events-none" />

      {/* Floating decorative orb */}
      <motion.div className="absolute -top-20 -left-20 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(166,193,168,0.25) 0%, transparent 70%)" }}
        animate={{ y: [0, 20, 0], x: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }} />
      <motion.div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(150,174,180,0.22) 0%, transparent 70%)" }}
        animate={{ y: [0, -20, 0], x: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }} />

      <div className="max-w-[1400px] mx-auto relative">
        <div className="text-center mb-12 sm:mb-16">
          <motion.p className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold mb-4"
            initial={{ opacity: 0, letterSpacing: "0.2em" }} whileInView={{ opacity: 1, letterSpacing: "0.5em" }}
            viewport={{ once: true }} transition={{ duration: 1 }}>
            {c("journeyEyebrow")}
          </motion.p>
          <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#464646] leading-[1.05] tracking-tight mb-5">
            <motion.span initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8 }}>
              {c("journeyHeadingPrefix")}
            </motion.span>
            <br />
            <RotatingWords words={c("journeyRotatingWords")} />
          </h2>
          <motion.p className="text-sm sm:text-base text-[#464646]/65 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 1.2, duration: 0.7 }}>
            {c("journeySubtext")}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 max-w-6xl mx-auto">
          {/* eBook */}
          <ProductCard testId="ebook" badge={c("productEbookBadge")} badgeColor="text-[#a89b94]"
            title={c("productEbookTitle")} sub={c("productEbookSub")}
            includes={c("productEbookIncludes")}
            price="$9.99" ctaLabel={c("productEbookCta")}
            icon={<ShoppingCart size={16} />}
            accent="sage"
            onBuy={() => setModal({ productId: "ebook", price: "$9.99", requireShipping: false,
              title: c("productEbookCta"), subtitle: "Calmasutras \u00b7 52 Ideas to Stay Calm" })}
          />

          {/* Bundle - Most Popular */}
          <ProductCard testId="bundle" badge={c("productBundleBadge")} badgeColor="text-[#96aeb4]" mostPopular
            title={c("productBundleTitle")} sub={c("productBundleSub")}
            includes={c("productBundleIncludes")}
            price="$33" originalPrice="$38.99" savings="Save $5.99"
            ctaLabel={c("productBundleCta")}
            icon={<Layers size={16} />}
            onBuy={() => setModal({ productId: "bundle", price: "$33", requireShipping: true,
              title: c("productBundleCta"), subtitle: "Deck + eBook" })}
          />

          {/* Deck - with qty stepper + tiered pricing + bulk inquiry */}
          <DeckCard
            onCheckout={(qty, unitPrice, totalPrice) => setModal({
              productId: "deck",
              price: `$${totalPrice}`,
              requireShipping: true,
              quantity: qty,
              title: qty > 1 ? `Buy ${qty} decks` : c("productDeckCta"),
              subtitle: qty > 1
                ? `Calmasutras \u00b7 ${qty} decks @ $${unitPrice}/deck`
                : "Calmasutras \u00b7 52 Cards",
            })}
            onBulkInquiry={(qty) => setBulkModal({ open: true, qty })}
          />
        </div>

        {/* Gifting CTA - bridge to /gift */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-10 sm:mt-12 max-w-3xl mx-auto" data-testid="gift-cta-card">
          <a href="/gift" className="group block">
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#587e62]/15 hover:border-[#587e62]/40 shadow-md hover:shadow-lg transition-all p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#a6c1a8] to-[#587e62] flex items-center justify-center shrink-0 shadow-md">
                <Gift size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#587e62] font-semibold mb-1">{c("giftBannerEyebrow")}</p>
                <p className="font-serif text-lg sm:text-xl font-bold text-[#464646] leading-tight">
                  {c("giftBannerHeadline")}
                </p>
                <p className="text-xs sm:text-sm text-[#464646]/65 mt-1">
                  {c("giftBannerBody")}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1.5 bg-[#464646] group-hover:bg-[#587e62] text-white rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors">
                {c("giftBannerCta")} <ArrowRight size={13} />
              </span>
            </div>
          </a>
        </motion.div>
      </div>

      <CheckoutModal open={!!modal} onClose={() => setModal(null)}
        title={modal?.title || ""} subtitle={modal?.subtitle || ""}
        productId={modal?.productId} price={modal?.price}
        requireShipping={!!modal?.requireShipping}
        quantity={modal?.quantity || 1} />

      <BulkInquiryModal open={bulkModal.open}
        onClose={() => setBulkModal({ open: false, qty: bulkModal.qty })}
        defaultQuantity={bulkModal.qty} />
    </section>
  );
}
